import { NgSignaturePadOptions, SignaturePadComponent } from '@almothafar/angular-signature-pad';
import {
    AfterViewInit,
    Component,
    ElementRef,
    forwardRef,
    HostListener,
    Inject,
    input,
    output,
    viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WINDOW } from '@app/shared/services/window.token';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-signature-field',
    templateUrl: './signature-field.component.html',
    styleUrls: ['./signature-field.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SignatureFieldComponent),
            multi: true,
        },
    ],
    imports: [
        SignaturePadComponent,
        MatIconButton,
        MatTooltip,
        MatIcon,
        TranslateModule,
    ],
})
export class SignatureFieldComponent
    implements ControlValueAccessor, AfterViewInit
{
    public signaturePad = viewChild<SignaturePadComponent>('signature');

    signatureWrapper =
        viewChild<ElementRef<HTMLDivElement>>('signatureWrapper');

    public _signature: any = null;
    public propagateChange: Function;

    canvasWidth = input<number>(380);
    canvasHeight = input<number>(200);
    showTruck = input<boolean>(false);
    send = input<boolean>(false);
    clearSignature = input<boolean>(false);
    padId = input<string>('');
    showPullSignature = input<boolean>(false);
    signatureCleared = output<boolean>();
    signatureDrawCompleted = output<boolean>();
    signatureDrawStarted = output<boolean>();
    pullSignature = output<boolean>();

    canvas: HTMLCanvasElement;
    wrapper: any;
    /** True between draw-start and draw-end of an actual stroke on the pad. */
    private isDrawing = false;

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        //  this.resizeCanvas();
    }

    loadSignature(): void {
        this.signaturePad()?.clear();
        this.resetPadState();
        this.pullSignature.emit(true);
    }

    public signaturePadOptions: NgSignaturePadOptions = {
        canvasWidth: this.canvasWidth(),
        canvasHeight: this.canvasHeight(),
    };
    constructor(
        public elRef: ElementRef,
        @Inject(WINDOW) public readonly window: any
    ) {
        this.window = window as Window;
    }

    ngAfterViewInit(): void {
        this.hardenPointerIdMatching();
        this.signaturePad()?.clear();
        this.resetPadState();
        if (this._signature) {
            this.signaturePad()?.fromDataURL(this._signature);
        }
        // this.resizeCanvas();
    }

    get signature(): any {
        return this._signature;
    }

    set signature(value: any) {
        this._signature = value;
        if (typeof this.propagateChange === 'function') {
            this.propagateChange(this.signature);
        }
    }

    public writeValue(value: any): void {
        if (!value) {
            return;
        }
        this._signature = value;
        if (this.signaturePad()?.getSignaturePad()) {
            this.signaturePad()!.fromDataURL(this.signature);
        }
    }

    public registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(): void {
        // no-op
    }

    drawComplete(event: MouseEvent | Touch) {
        const pad = this.signaturePad();
        // Don't report an empty pad as a completed signature.
        if (!pad || pad.isEmpty()) {
            return;
        }
        this.signature = pad.toDataURL();
        this.signatureDrawCompleted.emit(true);
    }

    drawStart(event: MouseEvent | Touch) {
        this.isDrawing = true;
        this.signatureDrawStarted.emit(true);
    }

    public clear(): void {
        this.signaturePad()!.clear();
        this.resetPadState();
        this.signature = '';
        this.signatureCleared.emit(true);
    }

    /**
     * Re-arms the underlying signature_pad after a clear.
     *
     * `SignaturePad.clear()` resets `_strokePointerId` to `undefined` but leaves
     * `_drawingStroke` at `true` when it runs while a stroke is still open (the
     * pad is cleared from `ngAfterViewInit`, the reset button and
     * `loadSignature`, and `redrawCanvas` clears again via
     * `changeBackgroundColor`). That combination is terminal: `pointermove` and
     * `pointerup` bail out in `_allowPointerId` because the stroke pointer id is
     * gone, so the stroke never ends, and every following `pointerdown` is
     * rejected by the `_drawingStroke` guard. The pad then looks alive but no
     * longer paints a single line until the component is rebuilt — not even the
     * reset button recovers it.
     *
     * `off()` drops the stale move/up listeners, `on()` re-binds `pointerdown`
     * and sets `_drawingStroke = false`, so the pad accepts input again. Uses
     * only public API of signature_pad.
     */
    private resetPadState(): void {
        const pad = this.signaturePad()?.getSignaturePad();
        if (!pad) {
            return;
        }
        pad.off();
        pad.on();
    }

    /**
     * Makes signature_pad match pointer events by `pointerId` alone.
     *
     * Since 5.0.9 signature_pad discards every pointer event whose id differs
     * from the one captured on `pointerdown`, and its `_getPointerId()` prefers
     * `PointerEvent.persistentDeviceId` over `pointerId`. Per MDN a device may
     * not report that hardware id in time for `pointerdown` — it is `0` there
     * and only becomes valid on later events. `_getPointerId()` then returns the
     * `pointerId` for the down event and the `persistentDeviceId` for every
     * move, so no move ever matches: nothing is drawn beyond the initial dot and
     * the pad deadlocks as described in `resetPadState`.
     *
     * `pointerId` is unique per active pointer, so matching on it alone keeps
     * the multi-pointer protection the id check was added for.
     */
    private hardenPointerIdMatching(): void {
        const pad = this.signaturePad()?.getSignaturePad() as unknown as {
            _getPointerId?: (event: PointerEvent) => number;
        };
        if (!pad) {
            return;
        }
        pad._getPointerId = (event: PointerEvent) => event.pointerId;
    }

    private resizeCanvas() {
        this.signaturePadOptions.canvasWidth = this.signaturePadWidth() - 2;
        this.signaturePad()!.set('canvasWidth', this.signaturePadWidth() - 2);
    }

    private signaturePadWidth() {
        const width = this.signatureWrapper()!.nativeElement.offsetWidth;
        if (width < 250) {
            return 250;
        }

        return 350; //width; // DIGLIEF-2644
    }
}
